import React, { useMemo } from 'react'
import { motion } from 'motion/react'
import { useRouter } from 'next/router'
import useNavigation, { JourneyStep } from 'src/lib/hooks/useNavigation'
import { useNavigationRBAC } from 'src/lib/utils/navigation-rbac'
import { useUser } from 'src/lib/contexts/UserContext'
import { isPathMatch } from 'src/lib/utils/path-matching'
import { classNames } from 'src/lib/utils/helpers'
import NavNewBadge from '../../NavNewBadge'

export interface NavigationItemType {
  id: string
  title: string
  path: string
  rbacPermission: string | string[]
  isSelected?: boolean
  showNewBadge?: boolean
}

export interface NavigationSectionType {
  title: string
  items: NavigationItemType[]
}

export interface DynamicNavigationProps {
  sections: NavigationSectionType[]
  type: JourneyStep
}

export const DynamicNavigation: React.FC<DynamicNavigationProps> = ({ sections, type }) => {
  const router = useRouter()
  const { user } = useUser()
  const {
    getters: { sidebarOpen, isTablet },
    actions: { handleSetJourneyStep, setOpenHoverCardId }
  } = useNavigation()
  const { filterSections } = useNavigationRBAC()

  const filteredSections = useMemo(() => filterSections(sections), [sections, filterSections, user])

  return (
    <div className='mt-0'>
      {filteredSections.map((section: NavigationSectionType, index: number) => (
        <div key={index} className='flex flex-col gap-1 py-2'>
          <motion.span layout className='text-text-xs-medium  text-zinc-500 px-4 py-1'>
            {section.title}
          </motion.span>
          <div className='px-2 flex flex-col gap-0.5'>
            {section.items.map((item: NavigationItemType) => {
              const isItemSelected = isPathMatch(router.asPath, item.path, isTablet)
              return (
                <motion.button
                  key={item.title}
                  layout
                  onClick={() => {
                    if (isTablet) {
                      setOpenHoverCardId(null)
                      handleSetJourneyStep(type)
                    }
                    router.push(item.path)
                  }}
                  id={item.id}
                  className={classNames(
                    `relative flex gap- px-2 py-1 w-full items-center rounded-md transition-colors text-zinc-600 text-sm h-[28px]`,
                    isItemSelected ? 'bg-zinc-950/10 text-zinc-800' : 'hover:bg-zinc-950/5'
                  )}
                >
                  {!sidebarOpen && (
                    <>
                      <motion.span className='text-text-sm-medium font-medium'>{item.title}</motion.span>
                      {item.showNewBadge && (
                        <div className='ml-auto flex shrink-0 items-center pl-1.5'>
                          <NavNewBadge />
                        </div>
                      )}
                    </>
                  )}
                </motion.button>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}
